import { useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";
import { useLenis } from "lenis/react";
import { HERO } from "@/data/site";
import Img from "@/components/site/Img";

const EASE = "cubic-bezier(0.16, 1, 0.3, 1)";

/**
 * Entrance animation is plain state + CSS, not framer-motion.
 *
 * Framer's mount animation was leaving the masked headline parked at its
 * initial translate, so the hero rendered empty. Anything that gates whether
 * content is visible now runs on a mount flag that fails *visible*.
 */
const useMounted = (delay = 60) => {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setReady(true), delay);
    return () => clearTimeout(t);
  }, [delay]);
  return ready;
};

const Line = ({ children, delay, ready }) => (
  <span className="type-mask">
    <span
      className="block"
      style={{
        transform: ready ? "translateY(0)" : "translateY(108%)",
        transition: `transform 1.2s ${EASE} ${delay}s`,
      }}
    >
      {children}
    </span>
  </span>
);

const Fade = ({ children, delay, ready, className = "" }) => (
  <div
    className={className}
    style={{
      opacity: ready ? 1 : 0,
      transform: ready ? "translateY(0)" : "translateY(16px)",
      transition: `opacity 0.9s ease ${delay}s, transform 0.9s ${EASE} ${delay}s`,
    }}
  >
    {children}
  </div>
);

export const Hero = () => {
  const lenis = useLenis();
  const ready = useMounted();

  const toWork = () => {
    const el = document.querySelector("#work");
    if (!el) return;
    if (lenis) lenis.scrollTo(el, { offset: -70 });
    else el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      data-testid="hero-section"
      className="relative bg-obsidian min-h-[86vh] md:min-h-[92vh] flex items-center"
    >
      <div className="absolute inset-0 overflow-hidden bg-onyx scrim-left scrim-top">
        <Img
          src={HERO.src}
          alt="Salvatore Ferragamo storefront at Yorkdale, Toronto — an illuminated stone facade with recessed vertical light strips, a warm-fin entry portal and twin display vitrines, delivered by G&F Customs"
          className="w-full h-full object-cover object-[center_25%]"
          priority
        />
      </div>

      <div className="relative w-full mx-auto max-w-shell px-6 md:px-10 pt-28 pb-16">
        <h1 className="font-display font-normal text-ivory leading-[1.0] tracking-[-0.015em] text-[clamp(2.4rem,6.2vw,5.8rem)] max-w-[15ch]">
          <Line delay={0.12} ready={ready}>
            {HERO.line1}
          </Line>
          <Line delay={0.24} ready={ready}>
            {HERO.line2}
          </Line>
        </h1>

        <Fade
          delay={0.5}
          ready={ready}
          className="mt-9 label text-ivory/80 max-w-[42ch] leading-[1.9]"
        >
          {HERO.body}
        </Fade>

        <Fade delay={0.66} ready={ready} className="mt-12">
          <button
            onClick={toWork}
            data-testid="hero-view-work"
            className="group inline-flex items-center gap-4 label text-ivory border-b border-ivory/50 pb-3 hover:border-champagne hover:text-champagne transition-colors duration-400"
          >
            {HERO.cta}
            <ArrowRight
              size={15}
              className="group-hover:translate-x-1.5 transition-transform duration-400"
            />
          </button>
        </Fade>
      </div>

      {/* Image credit, bottom right — the environment shown is the studio's own */}
      <Fade
        delay={0.85}
        ready={ready}
        className="absolute bottom-8 right-6 md:right-10 text-right hidden sm:block"
      >
        <div className="label-xs text-champagne">{HERO.credit[0]}</div>
        <div className="mt-1.5 label-xs text-ivory/50">{HERO.credit[1]}</div>
      </Fade>
    </section>
  );
};

export default Hero;
